import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { ApiError } from '../../../../shared/dtos/api-error';
import { ApiResponse } from '../../../../shared/dtos/api-response';
import {
  INTERNAL_PLWC_SUPPLIERS_ENDPOINT,
  INTERNAL_PLWC_SUPPLIER_ENDPOINT,
  INTERNAL_PLWC_SUPPLIER_SUSPEND_ENDPOINT,
} from '../constants/endpoints';
import { SupplierSummary, CompanyUpdateRequest } from '../../shared/dtos/supplier';
import { SuppliersStore } from '../../shared/stores/suppliers.store';
import { Supplier, SupplierMapper } from '../../shared/models/supplier';

@Injectable({ providedIn: 'root' })
export class SupplierService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(SuppliersStore);

  getSuppliers(): Observable<Supplier[]> {
    this.store.setLoading();
    return this.http.get<ApiResponse<SupplierSummary[]>>(INTERNAL_PLWC_SUPPLIERS_ENDPOINT).pipe(
      map((resp) => SupplierMapper.fromDtoList(resp.data)),
      tap((records) => this.store.setRecords(records)),
      catchError((err: HttpErrorResponse) => {
        const body = err.error as ApiError | undefined;
        this.store.setError(body?.message ?? 'Failed to load PLWC suppliers.');
        return throwError(() => body ?? { message: 'Failed to load PLWC suppliers.' });
      }),
    );
  }

  updateSupplier(companyId: number, body: CompanyUpdateRequest): Observable<Supplier> {
    return this.http
      .put<ApiResponse<SupplierSummary>>(INTERNAL_PLWC_SUPPLIER_ENDPOINT(companyId), body)
      .pipe(
        map((resp) => SupplierMapper.fromDto(resp.data)),
        tap((record) => this.store.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to update supplier.' });
        }),
      );
  }

  suspendSupplier(companyId: number): Observable<Supplier> {
    return this.http
      .put<ApiResponse<SupplierSummary>>(INTERNAL_PLWC_SUPPLIER_SUSPEND_ENDPOINT(companyId), {})
      .pipe(
        map((resp) => SupplierMapper.fromDto(resp.data)),
        tap((record) => this.store.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to suspend supplier.' });
        }),
      );
  }
}
